import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface EdgeMutationOutcome<T> {
  /** True when the edge function actually ran and returned data (business success or a
   *  detected already-applied no-op). False when the function could not be invoked at all
   *  (network/relay/fetch failure) and a client-side fallback may be attempted. */
  invoked: boolean;
  data: T | null;
  /** Present when the edge fn returned a non-2xx business error (validation, tenant mismatch,
   *  etc). This is a real failure the caller should surface — never a reason to silently run a
   *  client-side fallback that could double-post. `body` is the parsed JSON error response (when
   *  parseable) — Step 2 Phase C needs it to detect the 409 `{reverted:true}` reissue marker
   *  (bankreco-apply-recon-plan-step2.md §S2.2c(i″)(b)) without re-throwing a generic error. */
  error?: { message: string; status?: number; body?: Record<string, unknown> };
}

/**
 * Invokes a payment-mutations edge action and reports whether it was actually reached.
 * A non-2xx response (`FunctionsHttpError`) means the function ran and rejected the request —
 * that is a real error, not a "function missing, fall back to client path" situation. Only a
 * genuine invoke-level failure (fetch/relay error — the function could not be reached) should
 * ever trigger a client-side fallback.
 */
export async function invokePaymentMutationDetailed<T = any>(
  action: string,
  payload: Record<string, unknown>,
): Promise<EdgeMutationOutcome<T>> {
  try {
    const { data, error } = await supabase.functions.invoke("payment-mutations", {
      body: {
        action,
        payload,
        meta: {
          source: "frontend",
        },
      },
    });
    if (error) {
      if (error instanceof FunctionsHttpError) {
        // The edge function ran and returned a business error — never fall back to a
        // client-side re-implementation for this; that could double-post financial data.
        let message = error.message;
        let status: number | undefined;
        let body: Record<string, unknown> | undefined;
        try {
          body = await error.context.json();
          if (body?.error) message = body.error as string;
          status = error.context.status;
        } catch {
          // response body wasn't JSON / already consumed — use the generic message.
        }
        return { invoked: true, data: null, error: { message, status, body } };
      }
      // FunctionsFetchError / FunctionsRelayError — the function could not be reached at all.
      console.warn(`[Payments] ${action} via payment-mutations could not be reached, falling back`, error);
      return { invoked: false, data: null };
    }
    return { invoked: true, data: (data?.data ?? data ?? null) as T | null };
  } catch (err) {
    console.warn(`[Payments] ${action} via payment-mutations threw, falling back`, err);
    return { invoked: false, data: null };
  }
}

/**
 * Legacy convenience wrapper — kept for existing low-stakes callers (recon voucher creation,
 * processing-charge adjustment, error logging) that intentionally fall back to a client-side
 * path on ANY failure. Do not use this for `apply_reconciliation` or any action where a
 * client-side fallback could re-post financial data — use `invokePaymentMutationDetailed`
 * instead and only fall back on `invoked === false`.
 */
export async function invokePaymentMutation<T = any>(
  action: string,
  payload: Record<string, unknown>,
): Promise<T | null> {
  const outcome = await invokePaymentMutationDetailed<T>(action, payload);
  if (outcome.error) {
    console.warn(`[Payments] ${action} via payment-mutations failed, falling back`, outcome.error);
    return null;
  }
  return outcome.data;
}
